// biome-ignore lint/suspicious/noShadowRestrictedNames: arquero internal
import { escape } from 'arquero';
import { z } from 'zod';

import { NO_POS, type SourcePos } from './ast.ts';
import { colNodeLabels, pickColumnPath } from './column-ref.ts';
import type { EvalResult } from './env.ts';
import { bind, checkBindable, resolveSymbolWithSessionFallthrough } from './env.ts';
import { NestedDatasetCellError, ShapeError, TypeMismatchError, UnknownColumnRefError } from './errors.ts';
import { type AstInput, astShape, isOpCall, nodePos, opCallPos } from './normalize.ts';
import {
  type ColumnRefInArg,
  type CompileFn,
  defineScope,
  opMeta,
  opNameOf,
  type PropagateSchemaCtx,
  pipelineVerbs,
  type RawOpCall,
  type RowFn,
  type Scope,
  type SpecialCtx,
  type SpecialVerbCtx,
} from './op-meta.ts';
import { dateAwareEquals, toComparableNumber } from './ops/transform/date-utils.ts';
import { elemPath, nameKeyPath, paramPath } from './path.ts';
import { withTable } from './runtime/arquero.ts';
import { colListRefs, exprColRefs } from './schema/check.ts';
import { passThroughSchema, unifyColumnTypes } from './schema/infer.ts';
import {
  booleanCol,
  type ColumnType,
  describeColumnType,
  isNullable,
  nullableCol,
  numberCol,
  type Schema,
  schemaFromEntries,
  unknownCol,
  unwrapNullable,
} from './schema/types.ts';
import {
  ColumnRef,
  Dataset,
  expectDataset,
  expectLazy,
  isMaterialized,
  type MaterializedDataset,
  type Value,
} from './value.ts';

// Built-in ops as Zod schemas. The schema describes the op's name + arg
// shape; `.describe()` carries the LLM-facing summary; `.register(opMeta,
// ...)` attaches `apply` plus the registry-only fields (evaluation, effect,
// example, compileExpr).
//
// `apply`'s `data` argument is precisely typed per-schema via Zod's
// `$output` substitution — no `z.infer<typeof X>` annotation needed.
// `apply`'s RETURN is typed loosely (`unknown`/`Promise<unknown>`) in the
// registry-stored shape; authors annotate the arrow's return type
// (`: Value`, `: Promise<EvalResult>`, `: Dataset`, …) at the call site so
// the body is checked against the engine's runtime contract.

// Compile-helper: walk each positional arg of an op call to a RowFn. Used
// by every scalar op's compileExpr method. Generic `T` lets fixed-arity
// callers narrow the return to a tuple, e.g.
//   const [a, b] = compileArgs<readonly [RowFn, RowFn]>(call, compile, path);
// — safe because the schema validates arity before compileExpr runs.
const compileArgs = <T extends readonly RowFn[] = readonly RowFn[]>(
  call: RawOpCall,
  compile: CompileFn,
  path: string,
): T => (call.params ?? []).map((p, i) => compile(p, paramPath(path, i))) as unknown as T;

// Column-list refs helper: enumerate the symbol-refs in a single
// `[col col col …]` positional arg. Shared between verbs whose only
// column-bearing arg is the column list (`select`, `distinct`).
// The verb-meta hook `referencedColumns` is the SSOT for both
// unknown-ref diagnostics (§5.8 type-check pass) and per-cell dispatch
// detection (4-prep.3); same shared-helper pattern as `passThroughSchema`.
const colListReferences = (
  data: { readonly params: readonly [readonly unknown[]] },
  basePath: string,
): readonly ColumnRefInArg[] => colListRefs(data.params[0], paramPath(basePath, 0));

// Schema-fragment: comparison chain params (≥2). Shared by chain-comparison
// ops `<`, `<=`, `>`, `>=`. Operands are coerced date-aware via
// `toComparableNumber` (numeric semantics unchanged; ISO/offset date
// strings rescued to epoch-ms) so a `timestamp` column compares correctly
// against a string-literal date bound — same coercer the `compileExpr`
// (filter/derive per-row) path uses, keeping the two paths in sync.
const numChainParams = z.unknown().transform(toComparableNumber).array().min(2);

// Op-helpers: produce a paired `{ apply, compileExpr }` from a single
// reducer so apply (over schema-resolved values) and compileExpr (over
// per-row values) stay in sync. The optional `coerce` callback converts
// per-row values to the reducer's value type — schema-resolved values
// reach apply already coerced, so `coerce` is only applied on the
// compileExpr path.

// The `data` parameter of each helper-built `apply` is typed loosely as
// `{ params: readonly unknown[] }` (rather than `{ params: readonly
// ValueType[] }`) because the registry-stored apply signature is
// `(data: $output) => unknown` — function arg contravariance requires
// the helper accept whatever shape the registry passes. The schema
// validates params to the right ValueType before apply runs, so the
// internal cast is sound.
//
// `defineFold` is the structural meta-helper; the arity-specific variants
// (`defineUnary`, `defineBinary`, `defineChain`) are thin wrappers that
// supply a specialized reducer. The `values[i] as ValueType` casts inside
// each wrapper's reducer are safe because the op schema validates arity
// before either path runs.

/** Variadic fold (`+`, `*`, `and`, `or`, `coalesce`, `-`, `/`); also the
 * meta-helper that `defineUnary`/`defineBinary`/`defineChain` wrap.
 * `resultType` is the op's `ColumnType` for §5.8 pre-eval type check —
 * defaults to `unknownCol`; callers with a fixed result type (number
 * for arithmetic, boolean for comparison) pass the constant here. Ops
 * with computed types (e.g. `coalesce`) override the generated
 * `inferType` at the `.register(...)` site. */
export const defineFold = <ValueType = unknown>(
  reducer: (values: readonly ValueType[]) => Value,
  coerce: (value: unknown) => ValueType = (v) => v as ValueType,
  resultType: ColumnType = unknownCol,
) => ({
  apply: ({ params }: { readonly params: readonly unknown[] }): Value => reducer(params as readonly ValueType[]),
  compileExpr: (call: RawOpCall, compile: CompileFn, path: string): RowFn => {
    const args = compileArgs(call, compile, path);
    return (row) => reducer(args.map((f) => coerce(f(row))));
  },
  inferType: (): ColumnType => resultType,
});

/** Arity-1 scalar op (`not`, `is-null`, `is-not-null`). */
const defineUnary = <ValueType = unknown>(
  fn: (value: ValueType) => Value,
  coerce?: (value: unknown) => ValueType,
  resultType?: ColumnType,
) => defineFold<ValueType>((values) => fn(values[0] as ValueType), coerce, resultType);

/** Arity-2 scalar op (`mod`, `!=`). */
const defineBinary = <ValueType = unknown>(
  fn: (a: ValueType, b: ValueType) => Value,
  coerce?: (value: unknown) => ValueType,
  resultType?: ColumnType,
) => defineFold<ValueType>((values) => fn(values[0] as ValueType, values[1] as ValueType), coerce, resultType);

/** Variadic pairwise chain (`<`, `<=`, `>`, `>=`, `=`). True iff every adjacent
 * pair `(values[i-1], values[i])` satisfies `pairwise`. Chains always
 * return a boolean column — the default propagates without callers
 * needing to specify. */
const defineChain = <ValueType = unknown>(
  pairwise: (a: ValueType, b: ValueType) => boolean,
  coerce?: (value: unknown) => ValueType,
  resultType: ColumnType = booleanCol,
) =>
  defineFold<ValueType>(
    (values) => values.every((v, i) => i === 0 || pairwise(values[i - 1] as ValueType, v)),
    coerce,
    resultType,
  );

/** True iff `v` is null or undefined. SSOT for the engine's
 * "empty / missing value" test — an absent struct field or an
 * unfilled bucket cell arrives as `undefined`, not `null`, so every
 * null check treats the two alike (matches `isTruthy`'s falsy set). */
const isNullish = (v: unknown): boolean => v === null || v === undefined;
/** Null-propagating numeric op. Any null/undefined operand ⇒ `null`
 * (no throw — consistent with the `to-date`/`coalesce` null
 * convention); otherwise every arg is coerced to `number` and `fn` is
 * applied. Built on `defineFold` with the identity coerce so the
 * apply path (schema-resolved `unknown` params) and the compileExpr
 * path (per-row values) share one reducer. Result type is always
 * `number`. Schemas must use `z.unknown()` params (NOT
 * `z.coerce.number()`) so a null survives to the reducer instead of
 * being coerced to 0. */
const defineNumeric = (fn: (nums: readonly number[]) => number) =>
  defineFold<unknown>((vs) => (vs.some(isNullish) ? null : fn(vs.map(Number))), undefined, numberCol);

// Arithmetic.

const plus = z
  .object({
    name: z.literal('+'),
    params: z.coerce.number().array(),
  })
  .describe('Sum of zero or more numbers. (+) → 0.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(+ 1 2 3)', result: '6' },
    ...defineFold<number>((vs) => vs.reduce((s, n) => s + n, 0), Number, numberCol),
  });

const minus = z
  .object({
    name: z.literal('-'),
    params: z.coerce.number().array().min(1),
  })
  .describe('Subtraction. (- a) → -a; (- a b c) → a - b - c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(- 10 3 2)', result: '5' },
    ...defineFold<number>(
      (vs) => {
        const [first = 0, ...rest] = vs;
        if (rest.length === 0) {
          return -first;
        }
        return rest.reduce((acc, n) => acc - n, first);
      },
      Number,
      numberCol,
    ),
  });

const times = z
  .object({
    name: z.literal('*'),
    params: z.coerce.number().array(),
  })
  .describe('Product of zero or more numbers. (*) → 1.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(* 2 3 4)', result: '24' },
    ...defineFold<number>((vs) => vs.reduce((p, n) => p * n, 1), Number, numberCol),
  });

const divide = z
  .object({
    name: z.literal('/'),
    params: z.coerce.number().array().min(2),
  })
  .describe('Division chain. (/ a b c) → a / b / c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(/ 24 2 3)', result: '4' },
    ...defineFold<number>(
      (vs) => {
        const [first = 0, ...rest] = vs;
        return rest.reduce((acc, n) => acc / n, first);
      },
      Number,
      numberCol,
    ),
  });

const mod = z
  .object({
    name: z.literal('mod'),
    params: z.tuple([z.coerce.number(), z.coerce.number()]),
  })
  .describe('Remainder. (mod a b) → a mod b.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(mod 7 3)', result: '1' },
    ...defineBinary<number>((a, b) => a % b, Number, numberCol),
  });

const pow = z
  .object({
    name: z.literal('pow'),
    params: z.tuple([z.unknown(), z.unknown()]),
  })
  .describe('Exponentiation. (pow base exp) → base ** exp. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(pow 2 10)', result: '1024' },
    ...defineNumeric((n) => (n[0] as number) ** (n[1] as number)),
  });

const abs = z
  .object({ name: z.literal('abs'), params: z.tuple([z.unknown()]) })
  .describe('Absolute value. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(abs -5)', result: '5' },
    ...defineNumeric((n) => Math.abs(n[0] as number)),
  });

const neg = z
  .object({ name: z.literal('neg'), params: z.tuple([z.unknown()]) })
  .describe('Arithmetic negation. (neg x) → -x. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(neg 5)', result: '-5' },
    ...defineNumeric((n) => -(n[0] as number)),
  });

const round = z
  .object({ name: z.literal('round'), params: z.tuple([z.unknown()]) })
  .describe('Round to the nearest integer (half rounds toward +∞: (round -2.5) → -2). Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(round 2.5)', result: '3' },
    ...defineNumeric((n) => Math.round(n[0] as number)),
  });

const floor = z
  .object({ name: z.literal('floor'), params: z.tuple([z.unknown()]) })
  .describe('Round toward -∞. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(floor 2.9)', result: '2' },
    ...defineNumeric((n) => Math.floor(n[0] as number)),
  });

const ceil = z
  .object({ name: z.literal('ceil'), params: z.tuple([z.unknown()]) })
  .describe('Round toward +∞. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(ceil 2.1)', result: '3' },
    ...defineNumeric((n) => Math.ceil(n[0] as number)),
  });

const sqrt = z
  .object({ name: z.literal('sqrt'), params: z.tuple([z.unknown()]) })
  .describe('Square root. Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(sqrt 9)', result: '3' },
    ...defineNumeric((n) => Math.sqrt(n[0] as number)),
  });

const log = z
  .object({ name: z.literal('log'), params: z.tuple([z.unknown()]) })
  .describe('Natural logarithm (base e). Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(log 1)', result: '0' },
    ...defineNumeric((n) => Math.log(n[0] as number)),
  });

const exp = z
  .object({ name: z.literal('exp'), params: z.tuple([z.unknown()]) })
  .describe('Exponential (e ** x). Null arg ⇒ null.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(exp 0)', result: '1' },
    ...defineNumeric((n) => Math.exp(n[0] as number)),
  });

const minOp = z
  .object({
    name: z.literal('min'),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Scalar minimum of 2+ numbers (per-row, e.g. in derive/filter). Null arg ⇒ null. NOTE: the 1-arg aggregate `(min col)` is a separate op in the aggregation context.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(min 3 1 2)', result: '1' },
    ...defineNumeric((n) => Math.min(...n)),
  });

const maxOp = z
  .object({
    name: z.literal('max'),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Scalar maximum of 2+ numbers (per-row, e.g. in derive/filter). Null arg ⇒ null. NOTE: the 1-arg aggregate `(max col)` is a separate op in the aggregation context.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(max 3 1 2)', result: '3' },
    ...defineNumeric((n) => Math.max(...n)),
  });

// Comparison. `<`, `<=`, `>`, `>=`, `=` are chains; `!=` is strict pairwise.

const lessThan = z
  .object({ name: z.literal('<'), params: numChainParams })
  .describe('Strictly-less-than chain. (< a b c) is true iff a < b < c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(< 1 2 3)', result: 'true' },
    ...defineChain<number>((a, b) => a < b, toComparableNumber),
  });

const lessEq = z
  .object({ name: z.literal('<='), params: numChainParams })
  .describe('Less-than-or-equal chain. (<= a b c) is true iff a <= b <= c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(<= 1 1 2)', result: 'true' },
    ...defineChain<number>((a, b) => a <= b, toComparableNumber),
  });

const greater = z
  .object({ name: z.literal('>'), params: numChainParams })
  .describe('Strictly-greater-than chain. (> a b c) is true iff a > b > c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(> 3 2 1)', result: 'true' },
    ...defineChain<number>((a, b) => a > b, toComparableNumber),
  });

const greaterEq = z
  .object({ name: z.literal('>='), params: numChainParams })
  .describe('Greater-than-or-equal chain. (>= a b c) is true iff a >= b >= c.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(>= 3 3 2)', result: 'true' },
    ...defineChain<number>((a, b) => a >= b, toComparableNumber),
  });

// Equality is JS-strict (`===`) for number/string/boolean — `(= 1 "1")`
// is false. The ONE exception is dates: if either side is genuinely
// date-shaped (a `Date` cell or an ISO `YYYY-MM-DD…` string) the two
// are compared as instants, so `(= day "2025-10-01")` against a
// `timestamp` column matches instead of silently never matching.
const equals = z
  .object({
    name: z.literal('='),
    params: z.array(z.unknown()).min(2),
  })
  .describe(
    'Equality chain. (= a b c) is true iff a = b = c — strict for number/string/boolean; date-aware when either side is a date/timestamp.',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(= 1 1 1)', result: 'true' },
    ...defineChain((a, b) => dateAwareEquals(a, b)),
  });

const notEquals = z
  .object({
    name: z.literal('!='),
    params: z.tuple([z.unknown(), z.unknown()]),
  })
  .describe('Inequality. (!= a b) is true iff a ≠ b — the negation of `=` (strict, date-aware for dates/timestamps).')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(!= 1 2)', result: 'true' },
    ...defineBinary((a, b) => !dateAwareEquals(a, b), undefined, booleanCol),
  });
// Set membership. The second arg is a vector AST; eager evaluation
// resolves it to a JS array before reaching apply, and `compileExpr`'s
// vector branch produces the per-row array. CDF push-down (4a) maps
// `in` to its `in` filter leaf — see `expr/ops/knowledge-graph/pushdown.ts`.
const inOp = z
  .object({
    name: z.literal('in'),
    params: z.tuple([z.unknown(), z.array(z.unknown())]),
  })
  .describe(
    'Set membership. (in v [a b c]) is true iff v equals some element — same equality as `=` (strict, date-aware for dates/timestamps).',
  )
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(in "a" ["a" "b" "c"])', result: 'true' },
    ...defineBinary<unknown>(
      (value, members) => (members as readonly unknown[]).some((m) => dateAwareEquals(m, value)),
      undefined,
      booleanCol,
    ),
  });

// Stringify for prefix tests. A `Date` cell (a `timestamp` column)
// renders as its ISO-8601 string — so `(prefix createdTime "2025")`
// does the obvious thing instead of testing against the JS locale
// string `"Wed Oct 01 2025…"`. Everything else is plain `String(v)`,
// unchanged (ISO-string cells already startsWith correctly).
const prefixStringify = (v: unknown): string => (v instanceof Date ? v.toISOString() : String(v));

// String prefix test. Both arguments coerce to string at parse time
// (eager, via the `prefixStringify` schema transform) and at compile
// time (per-row coercion via `defineBinary`'s `coerce` callback) — the
// two paths share `prefixStringify` so they stay in sync. CDF push-down
// (4a) maps `prefix` to the `prefix` filter leaf.
const prefix = z
  .object({
    name: z.literal('prefix'),
    params: z.tuple([z.unknown().transform(prefixStringify), z.unknown().transform(prefixStringify)]),
  })
  .describe('String prefix test. (prefix str pat) is true iff str starts with pat.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(prefix "hello" "he")', result: 'true' },
    ...defineBinary<string>((str, pat) => str.startsWith(pat), prefixStringify, booleanCol),
  });

// Boolean.

const and = z
  .object({
    name: z.literal('and'),
    params: z.array(z.unknown()),
  })
  .describe('Logical AND. (and a b c) is true iff every arg is truthy. (and) → true.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(and true true false)', result: 'false' },
    ...defineFold<boolean>((vs) => vs.every((v) => v), Boolean, booleanCol),
  });

const or = z
  .object({
    name: z.literal('or'),
    params: z.array(z.unknown()),
  })
  .describe('Logical OR. (or a b c) is true iff any arg is truthy. (or) → false.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(or false false true)', result: 'true' },
    ...defineFold<boolean>((vs) => vs.some((v) => v), Boolean, booleanCol),
  });

const not = z
  .object({
    name: z.literal('not'),
    params: z.tuple([z.unknown()]),
  })
  .describe('Logical NOT. (not x) is true iff x is falsy.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(not false)', result: 'true' },
    ...defineUnary((v) => !v, undefined, booleanCol),
  });

// Null.

const isNull = z
  .object({
    name: z.literal('is-null'),
    params: z.tuple([z.unknown()]),
  })
  .describe('True iff the argument is null or missing (undefined — e.g. an absent struct field).')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(is-null null)', result: 'true' },
    ...defineUnary((v) => isNullish(v), undefined, booleanCol),
  });

const isNotNull = z
  .object({
    name: z.literal('is-not-null'),
    params: z.tuple([z.unknown()]),
  })
  .describe('True iff the argument is not null and not missing (undefined).')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(is-not-null 5)', result: 'true' },
    ...defineUnary((v) => !isNullish(v), undefined, booleanCol),
  });

const coalesce = z
  .object({
    name: z.literal('coalesce'),
    params: z.array(z.unknown()).min(1),
  })
  .describe('Return the first non-null, non-missing argument, or null if none.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(coalesce null null 3)', result: '3' },
    ...defineFold((vs) => {
      // `z.unknown()` widens to `{}` here — explicit cast restores `Value`.
      const found = vs.find((v) => !isNullish(v));
      return (found ?? null) as Value;
    }),
    // First non-null arg's type, with explicit nullable widening when
    // every arg is itself nullable. Strips `nullable<...>` wrappers
    // before unifying so two args of `nullable<number>` unify cleanly
    // to `number` (then re-wrap if all-nullable).
    inferType: (call, infer, path): ColumnType => {
      const params = call.params ?? [];
      const argTypes = params.map((p, i) => infer(p, paramPath(path, i)));
      const unified = unifyColumnTypes(argTypes.map(unwrapNullable));
      return argTypes.length > 0 && argTypes.every(isNullable) ? nullableCol(unified) : unified;
    },
  });

// Conditionals. Lisp truthiness — only `false`, `null`, `undefined`
// are falsy; `0`, `""`, `NaN` are truthy. Deliberately distinct from
// the JS-`!`-based truthiness of `not`/`and`/`or` so numeric/string
// columns don't silently fall through a branch. SSOT for the
// conditional-truthiness rule, shared by `if` and `case`.
const isTruthy = (v: unknown): boolean => v !== false && !isNullish(v);

// `if`/`case` are special forms so the non-taken branch is NOT
// evaluated at top level (true short-circuit via `ctx.evalNode`).
// Each also carries a `compileExpr` so it composes inside
// derive/filter expressions (per-row; both branches are compiled but
// only the taken one runs per row) and an `inferType` for §5.8
// schema propagation.
const ifOp = z
  .object({ name: z.literal('if'), params: z.array(z.unknown()).min(2).max(3) })
  .describe(
    'Conditional. (if cond then [else]) — lisp truthiness (only false/null/undefined are falsy; 0, "", NaN are truthy). Missing else ⇒ null. The non-taken branch is not evaluated.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(if (> 3 1) "y" "n")', result: '"y"' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const cond = (await ctx.evalNode(data.params[0] as AstInput, ctx.env, paramPath(ctx.path, 0))).value;
      if (isTruthy(cond)) {
        return {
          value: (await ctx.evalNode(data.params[1] as AstInput, ctx.env, paramPath(ctx.path, 1))).value,
          env: ctx.env,
        };
      }
      const elseNode = data.params[2];
      if (elseNode === undefined) {
        return { value: null, env: ctx.env };
      }
      return {
        value: (await ctx.evalNode(elseNode as AstInput, ctx.env, paramPath(ctx.path, 2))).value,
        env: ctx.env,
      };
    },
    compileExpr: (call, compile, path): RowFn => {
      const params = call.params ?? [];
      const cond = compile(params[0], paramPath(path, 0));
      const thenFn = compile(params[1], paramPath(path, 1));
      const elseRaw = params[2];
      const elseFn = elseRaw === undefined ? undefined : compile(elseRaw, paramPath(path, 2));
      return (row) => {
        if (isTruthy(cond(row))) {
          return thenFn(row);
        }
        return elseFn === undefined ? null : elseFn(row);
      };
    },
    inferType: (call, infer, path): ColumnType => {
      const params = call.params ?? [];
      const thenT = unwrapNullable(infer(params[1], paramPath(path, 1)));
      const elseRaw = params[2];
      const elseT = elseRaw === undefined ? unknownCol : unwrapNullable(infer(elseRaw, paramPath(path, 2)));
      const unified = unifyColumnTypes([thenT, elseT]);
      return elseRaw === undefined ? nullableCol(unified) : unified;
    },
  });

// A `case` clause is a 2-element vector `[cond val]`; a trailing
// 1-element vector `[default]` is the fallback. Vector clauses (not
// paren `(else …)`) because the s-expr parser reserves a paren list's
// head for an op name — documented deviation from the §10.7 sketch,
// same semantics, parser-safe. `parseCaseClause` is the SSOT for the
// clause shape rule; `apply`, `compileExpr`, and `inferType` all
// consume the normalized discriminated form (no per-method re-parse).
type CaseClause =
  | { readonly kind: 'default'; readonly val: AstInput }
  | { readonly kind: 'clause'; readonly cond: AstInput; readonly val: AstInput };

const parseCaseClause = (node: AstInput, index: number, pos: SourcePos): CaseClause => {
  const shape = astShape(node);
  if (shape.kind !== 'vector' || shape.elements.length < 1 || shape.elements.length > 2) {
    throw new ShapeError('case clauses must be [cond val] vectors with a trailing [default] vector', pos, `#${index}`);
  }
  const [a, b] = shape.elements;
  return b === undefined
    ? { kind: 'default', val: a as AstInput }
    : { kind: 'clause', cond: a as AstInput, val: b as AstInput };
};

const caseOp = z
  .object({ name: z.literal('case'), params: z.array(z.unknown()).min(1) })
  .describe(
    'Multi-branch conditional. (case [cond val] [cond val] … [default]) — clauses are 2-element vectors evaluated top-down; the first truthy cond wins. A trailing 1-element vector is the default. No match and no default ⇒ null. Lisp truthiness; non-taken branches are not evaluated.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(case [(> 5 10) "big"] [(> 5 1) "mid"] ["small"])', result: '"mid"' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const callPos = opCallPos(ctx.spans, ctx.path);
      for (let i = 0; i < data.params.length; i++) {
        const clause = parseCaseClause(data.params[i] as AstInput, i, callPos);
        const clausePath = paramPath(ctx.path, i);
        if (clause.kind === 'default') {
          return { value: (await ctx.evalNode(clause.val, ctx.env, elemPath(clausePath, 0))).value, env: ctx.env };
        }
        const cond = (await ctx.evalNode(clause.cond, ctx.env, elemPath(clausePath, 0))).value;
        if (isTruthy(cond)) {
          return { value: (await ctx.evalNode(clause.val, ctx.env, elemPath(clausePath, 1))).value, env: ctx.env };
        }
      }
      return { value: null, env: ctx.env };
    },
    compileExpr: (call, compile, path): RowFn => {
      const params = call.params ?? [];
      const clauses = params.map((p, i) => {
        const clause = parseCaseClause(p as AstInput, i, NO_POS);
        const cp = paramPath(path, i);
        return clause.kind === 'default'
          ? ({ kind: 'default', val: compile(clause.val, elemPath(cp, 0)) } as const)
          : ({
              kind: 'clause',
              cond: compile(clause.cond, elemPath(cp, 0)),
              val: compile(clause.val, elemPath(cp, 1)),
            } as const);
      });
      return (row) => {
        for (const c of clauses) {
          if (c.kind === 'default') {
            return c.val(row);
          }
          if (isTruthy(c.cond(row))) {
            return c.val(row);
          }
        }
        return null;
      };
    },
    inferType: (call, infer, path): ColumnType => {
      const params = call.params ?? [];
      const valTypes = params.map((p, i) => {
        const clause = parseCaseClause(p as AstInput, i, NO_POS);
        const cp = paramPath(path, i);
        return clause.kind === 'default'
          ? unwrapNullable(infer(clause.val, elemPath(cp, 0)))
          : unwrapNullable(infer(clause.val, elemPath(cp, 1)));
      });
      const unified = unifyColumnTypes(valTypes.length > 0 ? valTypes : [unknownCol]);
      const hasDefault = params.some((p, i) => parseCaseClause(p as AstInput, i, NO_POS).kind === 'default');
      return hasDefault ? unified : nullableCol(unified);
    },
  });

const nullIf = z
  .object({ name: z.literal('null-if'), params: z.tuple([z.unknown(), z.unknown()]) })
  .describe('(null-if x sentinel) — null when x is strictly equal (===) to sentinel, otherwise x.')
  .register(opMeta, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(null-if 5 5)', result: 'null' },
    ...defineBinary((a, b) => (a === b ? null : (a as Value))),
    inferType: (call, infer, path): ColumnType =>
      nullableCol(unwrapNullable(infer((call.params ?? [])[0], paramPath(path, 0)))),
  });

// Symbol reference shape used by `dataset` and pipeline verbs to capture
// names structurally (rather than via env lookup). The parser emits symbols
// as `{ $sym: <name> }`; bindings/verbs accept that shape without
// evaluating it.
// biome-ignore lint/style/useNamingConvention: $-prefixed engine reserved
const SymbolRef = z.object({ $sym: z.string() });

// Schema fragment: an arbitrary `AstInput` value. Used in special-form and
// special-verb schemas (`dataset`, `filter`, `derive`) whose arg is raw AST
// rather than a resolved value. Zod has no first-class union for the
// AstInput shape, so the cast asserts the schema accepts the engine's
// JS-object AST surface.
const astInputSchema = z.unknown() as z.ZodType<AstInput>;

// Schema fragment: a tuple of one column-name list (`[[<sym> ...]]`).
// Shared by `select` and `distinct` — both project/keep rows by an
// ordered list of column references.
const colListParams = z.tuple([z.array(SymbolRef).min(1)]);

// Top-level forms.

const dataset = z
  .object({
    name: z.literal('dataset'),
    params: z.tuple([SymbolRef, astInputSchema]),
  })
  .describe(
    'Bind a Dataset value produced by `body` to `bindingName` in the current scope. The binding is visible to subsequent top-level forms. The body must evaluate to a Dataset — non-Dataset values are rejected with a TypeError.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(dataset wells (query-knowledge-graph "WellAsset" :where (= type "production") :limit 100))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [nameRef, body] = data.params;
      const namePos = nodePos(ctx.spans, paramPath(ctx.path, 0));
      // Validate the binding name before evaluating the right-hand side, so
      // op-name shadowing and rebinding fail before any (possibly expensive)
      // body evaluation runs. Type-check fires only on a structurally valid
      // binding.
      checkBindable(ctx.env, nameRef.$sym, namePos);
      const bodyPath = paramPath(ctx.path, 1);
      const { value } = await ctx.evalNode(body, ctx.env, bodyPath);
      const ds = expectDataset(value, `(dataset ${nameRef.$sym} ...)`, nodePos(ctx.spans, bodyPath));
      const env = bind(ctx.env, nameRef.$sym, ds, namePos);
      // Persist the binding as a recipe (the unevaluated RHS `body`),
      // so it survives this `evalExpr` call and a page reload. Same
      // single source of truth the tool layer's `recordBinding` feeds;
      // throws `SessionRebindingError` / `ShadowingOpError` on conflict
      // (immutable cross-turn bindings keep prior `(col …)` refs stable).
      ctx.evalCtx.session.recordBinding(nameRef.$sym, body, ds);
      return { value: ds, env };
    },
  });
/**
 * `(materialize ds)` — D5 boundary #4: the explicit force.
 *
 * Special op: re-evaluates its argument, then forces materialisation
 * if the result is a `LazyDataset`. Pass-through if the result is
 * already materialised (no-op identity). Errors out via
 * `TypeMismatchError` if the result isn't a `Dataset` at all.
 *
 * Routes through `ctx.materialize` (a `SpecialCtx` callback the
 * evaluator binds to `materialize.ts`'s `materialize()` with the
 * surrounding script's `ops`/`verbs` already curried in). The callback
 * indirection keeps `builtins.ts` decoupled from `materialize.ts` —
 * if we imported `materialize()` directly here, `builtins.ts` →
 * `materialize.ts` → `evaluator.ts` → `builtins.ts` would close a
 * load-order cycle that vitest's ESM loader can't resolve.
 *
 * Implementation lives in `materialize.ts`; this file owns only the
 * op-surface registration so `materializeOpForm` participates in the
 * canonical `builtins` array next to every other top-level op.
 */
const materializeOpForm = z
  .object({
    name: z.literal('materialize'),
    params: z.tuple([z.unknown()]),
  })
  .describe('Force materialisation of a (possibly lazy) Dataset. Returns the materialised Dataset.')
  .register(opMeta, {
    evaluation: 'special',
    effect: 'io',
    example: { expr: '(materialize wells)' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const dsPath = paramPath(ctx.path, 0);
      const { value } = await ctx.evalNode(data.params[0] as AstInput, ctx.env, dsPath);
      const ds = expectDataset(value, '(materialize <Dataset>)', nodePos(ctx.spans, dsPath));
      // `isMaterialized` narrowing pairs with `Dataset`'s two-variant
      // discrimination: the `else` branch is guaranteed `LazyDataset`
      // by the type system (covered by `value.test.ts`'s discrimination
      // suite). `expectLazy` makes the narrowing explicit to TS and
      // fails loudly if a future third Dataset state appears.
      if (isMaterialized(ds)) {
        return { value: ds, env: ctx.env };
      }
      const lazy = expectLazy(ds, '(materialize <Dataset>)', nodePos(ctx.spans, dsPath));
      const result = await ctx.materialize(lazy);
      return { value: result, env: ctx.env };
    },
  });
/**
 * `(col ...)` — polymorphic column reference (Phase 4-prep sub-task
 * 4-prep.6, PHASE_4_PREP.md D9). Variadic positional params, min 2.
 * Three call shapes discriminated by the first arg's `astShape`:
 *
 *   - **Form 1** (named binding) — `(col foo-data revenue)`: first arg
 *     is a `symbolRef`; the symbol's name is captured *verbatim* as the
 *     `ds: string` field (NOT looked up to get its value — preserves
 *     symbolic ref for deferred resolution). Subsequent args are
 *     column name + struct path.
 *   - **Form 2** (expression) — `(col (last foo-data) "fooTsCol")`:
 *     first arg is an op-call; eagerly evaluate it via
 *     `ctx.evalNode` to obtain a Dataset value and store as
 *     `ds: Dataset`.
 *   - **Form 3** (struct path) — `(col foo-data location coords lat)`:
 *     same as Form 1/2 but with ≥3 args; args 3+ become the struct
 *     `path`. Path support is uniform across Forms 1 and 2.
 *
 * Column-name and path args accept either a `symbolRef` (using its
 * `$sym` text) or a string primitive — these are name literals, not
 * looked-up values.
 *
 * Apply-time validation:
 *   - For both forms, the dataset is resolved (Form 1: via Env →
 *     Session; Form 2: by evaluating the expression) so the column
 *     and struct path can be checked against the schema. Missing
 *     dataset binding → `UnboundSymbolError`; non-Dataset binding →
 *     `TypeMismatchError`; missing column → `UnknownColumnRefError`;
 *     path walks into a non-`struct` type → `TypeMismatchError`.
 *   - The ColumnRef stores `ds` symbolically (string for Form 1, the
 *     Dataset value for Form 2). Consumers that need the column data
 *     project the dataset themselves (path-walking is per-cell
 *     consumer logic, not column-level).
 *
 * Typecheck-pass support is deferred — the current `typeCheckScript`
 * only walks `(transform ...)` and `(dataset ...)`. Generalising to
 * arbitrary top-level expressions is its own piece of work.
 */
const colOpForm = z
  .object({
    name: z.literal('col'),
    params: z.array(z.unknown()).min(2, {
      error:
        '`(col …)` needs at least 2 arguments — `(col <dataset|binding> <column> [<path>…])` — because it extracts a COLUMN, not a dataset. To pass a whole bound dataset (e.g. as the input to `transform`) reference it by its bare binding name, not `(col <name>)`.',
    }),
  })
  .describe(
    'Column reference (polymorphic). Three call shapes: ' +
      '`(col <name> <column> [<path>...])` — by binding name; ' +
      '`(col <expr> <column> [<path>...])` — by evaluated Dataset expression; ' +
      'path args walk into nested struct columns.',
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: [
      { expr: '(col foo-data revenue)' },
      { expr: '(col (last foo-data) "fooTsCol")' },
      { expr: '(col foo-data location coords lat)' },
    ],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [first, second, ...rest] = data.params as readonly unknown[];
      const callPos = opCallPos(ctx.spans, ctx.path);

      // Discriminate first arg: symbol → Form 1 (preserve name as string);
      // anything else → Form 2 (evaluate via `ctx.evalNode`, expect a
      // Dataset). This covers op-calls (`(last foo-data)`), inline
      // Dataset literals (test usage), and surfaces clean errors for
      // wrong-kind inputs (e.g., a number) via `expectDataset`.
      const firstShape = astShape(first);
      const { dsForRef, dsForValidation } =
        firstShape.kind === 'symbolRef'
          ? resolveNamedDs(firstShape.sym, ctx, callPos)
          : await resolveExprDs(first as AstInput, ctx, paramPath(ctx.path, 0), callPos);

      // Column name + path: each accepts either symbolRef or string.
      const column = extractColumnLabel(second, ctx.spans, paramPath(ctx.path, 1), 'column name');
      const path = rest.map((p, i) =>
        extractColumnLabel(p, ctx.spans, paramPath(ctx.path, 2 + i), `path segment ${i}`),
      );

      // Validate column + struct path against schema.
      validateColumnPath(dsForValidation, column, path, callPos);

      return { value: new ColumnRef(dsForRef, column, path), env: ctx.env };
    },
    // Expression context (`derive` body / `filter` predicate): exactly
    // one dataset is in scope — the current row — so a leading binding
    // name is irrelevant. This mirrors the join verb's role-based
    // `:on` rule via the shared `pickColumnPath`: the first label that
    // names a column on the row is the column, preceding labels are an
    // ignored binding prefix, trailing labels the struct sub-path.
    //
    // Fail-fast on a typo'd name — no silent coerce-to-null. If no
    // label resolves to a column on the row, or a struct sub-path
    // segment is absent on a record cell, throw `UnknownColumnRefError`
    // — whose `suggestions()` lists the available columns / sibling
    // fields as the engine's "did you mean". `exprColRefs` +
    // `validateRefs` apply the same role-based rule against the static
    // `Schema`, so a statically-known input surfaces the identical
    // diagnostic *before* any I/O; this row-time guard is the
    // defence-in-depth for lazy / qkg inputs whose schema isn't known
    // at type-check time (`resolveDsArg` → undefined).
    // (No `inferType`: the §5.8 `InferFn` contract has no schema
    // access, so the column's struct-field type can't be resolved
    // here — the result type is conservatively `unknown`, exactly as
    // for `transform` / `join` outputs.)
    compileExpr: (call): RowFn => {
      const labels = colNodeLabels(call);
      if (labels === undefined) {
        throw new ShapeError('`(col …)` labels in an expression must be symbols or string literals', NO_POS);
      }
      const rendered = `(col ${labels.join(' ')})`;
      return (row) => {
        const picked = pickColumnPath(labels, (n) => Object.hasOwn(row, n));
        if (picked === undefined) {
          // No label names a column on the row — a typo, not a null.
          // Surface every available column as the "did you mean".
          throw new UnknownColumnRefError(rendered, Object.keys(row), NO_POS);
        }
        const base = row[picked.column];
        if (base instanceof Dataset) {
          throw new NestedDatasetCellError(picked.column, NO_POS);
        }
        // Validating struct-path walk (replaces the silent
        // `walkStructPath`): a missing key on a *record* cell is a
        // typo'd sub-field — surface its siblings as "did you mean".
        // A genuine `null` along the path is short-circuited to `null`
        // (not a typo); a sub-path into a scalar/array preserves
        // `walkStructPath`'s silent semantics (scope: name typos only).
        let cur: unknown = base;
        for (const seg of picked.path) {
          if (cur == null) {
            return null;
          }
          if (typeof cur === 'object' && !Array.isArray(cur) && !(cur instanceof Dataset) && !Object.hasOwn(cur, seg)) {
            throw new UnknownColumnRefError(seg, Object.keys(cur), NO_POS);
          }
          cur = (cur as Record<string, unknown>)[seg];
        }
        return cur;
      };
    },
  });

/**
 * Form 1 helper for `(col ...)`: resolve a binding-name `ds` argument.
 * Returns the symbolic `dsForRef: string` (for the ColumnRef payload)
 * and a `dsForValidation: Dataset` (the resolved value, used only at
 * apply-time to validate the column + path exist in the schema).
 * Env → Session fallthrough is provided by the shared
 * `resolveSymbolWithSessionFallthrough` helper in `env.ts`, which is
 * also used by `evalNode`'s `symbolRef` case (Phase 4-prep 4-prep.7).
 */
const resolveNamedDs = (
  name: string,
  ctx: SpecialCtx,
  callPos: ReturnType<typeof opCallPos>,
): { readonly dsForRef: string; readonly dsForValidation: Dataset } => {
  const value = resolveSymbolWithSessionFallthrough(ctx.env, ctx.evalCtx.session, name, callPos);
  return { dsForRef: name, dsForValidation: expectDataset(value, `(col ${name} ...)`, callPos) };
};

/**
 * Form 2 helper for `(col ...)`: evaluate the first arg as an
 * expression and expect a Dataset. Both `dsForRef` and `dsForValidation`
 * are the same Dataset value — the expression form has no symbolic
 * binding name to preserve.
 */
const resolveExprDs = async (
  expr: AstInput,
  ctx: SpecialCtx,
  exprPath: string,
  callPos: ReturnType<typeof opCallPos>,
): Promise<{ readonly dsForRef: Dataset; readonly dsForValidation: Dataset }> => {
  const result = await ctx.evalNode(expr, ctx.env, exprPath);
  const ds = expectDataset(result.value, '(col <expr> ...)', callPos);
  return { dsForRef: ds, dsForValidation: ds };
};

/**
 * Extract a column-label (column name or path segment) from a node
 * arg. `(col foo-data revenue)` uses a symbol; `(col foo-data "revenue")`
 * uses a string literal — both produce the same `'revenue'` label.
 * Anything else (number, vector, op-call, ...) is a shape error
 * because column labels are name literals, not expressions.
 */
const extractColumnLabel = (
  node: unknown,
  spans: ReturnType<typeof opCallPos> extends ReturnType<infer S> ? Parameters<S>[0] : never,
  path: string,
  role: string,
): string => {
  const shape = astShape(node);
  if (shape.kind === 'symbolRef') {
    return shape.sym;
  }
  if (shape.kind === 'primitive' && typeof shape.value === 'string') {
    return shape.value;
  }
  throw new ShapeError(`expected a symbol or string literal as the ${role} of \`col\``, nodePos(spans, path));
};

/**
 * Validate a `(col ...)` reference against the source Dataset's
 * schema. Three failure modes: column not in schema (raises
 * `UnknownColumnRefError`); path tries to walk into a non-`struct`
 * type (raises `TypeMismatchError`); path segment isn't a known
 * field of the current struct (raises `UnknownColumnRefError`).
 *
 * `nullable(...)` wrappers are unwrapped transparently — refs into a
 * nullable struct column behave the same as refs into the underlying
 * struct. Null-cell semantics are a runtime concern of the consumer,
 * not a typecheck concern here.
 */
export const validateColumnPath = (
  ds: Dataset,
  column: string,
  path: readonly string[],
  pos: ReturnType<typeof opCallPos>,
): void => {
  const colType = ds.schema.get(column);
  if (colType === undefined) {
    throw new UnknownColumnRefError(column, Array.from(ds.schema.keys()), pos);
  }
  let curType: ColumnType = unwrapNullable(colType);
  for (const segment of path) {
    if (curType.kind !== 'struct') {
      throw new TypeMismatchError(
        `a column of ColumnType \`${describeColumnType(curType)}\``,
        'a struct column (path walking requires struct fields)',
        `(col ... ${segment})`,
        pos,
      );
    }
    const fieldType: ColumnType | undefined = curType.fields[segment];
    if (fieldType === undefined) {
      throw new UnknownColumnRefError(segment, Object.keys(curType.fields), pos);
    }
    curType = unwrapNullable(fieldType);
  }
};

// Pipeline verbs. Registered in `pipelineVerbs` (not `opMeta`) — only
// reachable through `transform`. Calling any of these at top level
// surfaces `UnknownOpError`.

const filter = z
  .object({
    name: z.literal('filter'),
    params: z.tuple([astInputSchema]),
  })
  .describe('Keep rows where the predicate evaluates true.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (filter (> price 100)))' },
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset => {
      const [pred] = data.params;
      const rowFn = ctx.compile(pred, paramPath(ctx.path, 0));
      return withTable(dataset, (t) => t.filter(escape(rowFn)));
    },
    // Row-level filter: columns are unchanged.
    propagateSchema: passThroughSchema,
    // The predicate's column refs drive both §5.8 unknown-ref
    // diagnostics and per-cell dispatch — a predicate that references
    // a `nested-dataset` column lifts `filter` per-cell automatically
    // (Phase 4-prep, sub-task 4-prep.3).
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] =>
      exprColRefs(data.params[0], paramPath(basePath, 0)),
  });

// Internal cast target for derive's args. The Zod schema validates the
// shape; storage signature widens to `unknown` so both `propagateSchema`
// and `referencedColumns` need a structural cast back to this shape.
// Declared once here so the two methods share a single source of truth.
type DerivePair = { readonly name: string; readonly params: readonly [unknown] };

const derive = z
  .object({
    name: z.literal('derive'),
    // Each pair is parsed as `(colname expr)` → `{name: colname, params: [expr]}`.
    params: z
      .array(
        z.object({
          name: z.string(),
          params: z.tuple([astInputSchema]),
        }),
      )
      .min(1),
  })
  .describe('Add one or more derived columns. Variadic positional (colname expr) pairs.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (derive (gross (* qty price)) (margin (/ gross cost))))' },
    // Pairs are evaluated in declaration order, each as its own chained
    // `t.derive({name: fn})`. Chaining (rather than one combined
    // `t.derive({...})`) materialises each new column before the next
    // pair's expression runs, so a later pair can reference a
    // column computed by an earlier pair in the SAME derive call.
    apply: (data, dataset: MaterializedDataset, ctx: SpecialVerbCtx): MaterializedDataset =>
      withTable(dataset, (t) =>
        data.params.reduce((acc, pair, i) => {
          const exprPath = paramPath(paramPath(ctx.path, i), 0);
          return acc.derive({ [pair.name]: escape(ctx.compile(pair.params[0], exprPath)) });
        }, t),
      ),
    // Each new column appends an entry whose `ColumnType` is recovered
    // from the right-hand expression via `inferExprType`. Input
    // columns are preserved verbatim; new columns follow them in
    // declaration order. The schema is folded so pair `i` is inferred
    // against the input schema PLUS columns added by pairs `0..i-1`
    // (passed as the `inferExprType` schema override) — keeps a pair
    // referencing an earlier-derived column precisely typed instead of
    // degrading to `unknown`.
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema =>
      (data.params as readonly DerivePair[]).reduce<Schema>((schema, pair, i) => {
        const exprPath = paramPath(paramPath(ctx.path, i), 0);
        const colType = ctx.inferExprType(pair.params[0], exprPath, schema);
        return schemaFromEntries([...schema, [pair.name, colType]]);
      }, ctx.inputSchema),
    // Each pair's right-hand expression contributes its column refs.
    // A pair's LHS name is a DEFINITION, not a usage — and that binding
    // is in scope for every LATER pair in the same derive call (scope
    // accumulates, see `apply`). So a ref naming a column defined by an
    // earlier pair is an intra-derive definition, not an external
    // column usage, and must stay out of the ref set — otherwise the
    // §5.8 type-check pass mis-flags it as `UnknownColumnRefError`
    // (it validates refs against the verb's INPUT schema). Pairs are
    // positional within derive; each expr is at
    // `<verbPath>/params/<pairIdx>/0`.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
      const definedByPriorPairs = new Set<string>();
      return (data.params as readonly DerivePair[]).flatMap((pair, i) => {
        const refs = exprColRefs(pair.params[0], paramPath(paramPath(basePath, i), 0)).filter(
          (ref) => !definedByPriorPairs.has(ref.name),
        );
        definedByPriorPairs.add(pair.name);
        return refs;
      });
    },
  });

const select = z
  .object({
    name: z.literal('select'),
    params: colListParams,
  })
  .describe('Project to the listed columns, in that order.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (select [name price]))' },
    apply: (data, dataset: MaterializedDataset): MaterializedDataset => {
      const colNames = data.params[0].map((c) => c.$sym);
      return withTable(dataset, (t) => t.select(...colNames));
    },
    // Output schema = input schema filtered to the listed columns in
    // declared order. Missing column refs fall back to `unknown` — the
    // §5.8 pre-eval type-check pass surfaces them as diagnostics; this
    // method itself stays total so it can run during propagation.
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const colNames = data.params[0].map((c: { readonly $sym: string }) => c.$sym);
      return schemaFromEntries(colNames.map((name: string) => [name, ctx.inputSchema.get(name) ?? unknownCol]));
    },
    // The listed columns are USAGES (projections). A nested-dataset
    // column in the list will trip per-cell dispatch (4-prep.3); the
    // per-cell select on the cell's inner schema then errors with
    // `UnknownColumnRefError` for any name not in the inner schema —
    // semantically equivalent to a runtime ref check.
    referencedColumns: colListReferences,
  });

const limit = z
  .object({
    name: z.literal('limit'),
    params: z.tuple([z.coerce.number().int().nonnegative()]),
  })
  .describe('Keep the first N rows.')
  .register(pipelineVerbs, {
    evaluation: 'eager',
    effect: 'pure',
    example: { expr: '(transform ds (limit 5))' },
    apply: ({ params }, dataset: MaterializedDataset): MaterializedDataset =>
      withTable(dataset, (t) => t.slice(0, params[0])),
    propagateSchema: passThroughSchema,
  });

const distinct = z
  .object({
    name: z.literal('distinct'),
    params: colListParams,
  })
  .describe('Keep rows with distinct combinations of the listed columns.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (distinct [region year]))' },
    apply: (data, dataset: MaterializedDataset): MaterializedDataset => {
      const colNames = data.params[0].map((c) => c.$sym);
      return withTable(dataset, (t) => t.dedupe(...colNames));
    },
    propagateSchema: passThroughSchema,
    // The listed columns are USAGES (the dedup key). Shared helper with
    // `select` — both have an `[col col col …]` sole arg.
    referencedColumns: colListReferences,
  });

// Internal cast target for rename's args. Same SSOT pattern as
// `DerivePair`: the Zod schema validates the shape, but the registry-
// stored signature widens, so `propagateSchema` and `referencedColumns`
// both cast back to this shape from one declaration.
type RenamePair = { readonly name: string; readonly params: readonly [{ readonly $sym: string }] };

const rename = z
  .object({
    name: z.literal('rename'),
    // Each pair is `(old new)` → `{name: old, params: [{$sym: new}]}`.
    params: z
      .array(
        z.object({
          name: z.string(),
          params: z.tuple([SymbolRef]),
        }),
      )
      .min(1),
  })
  .describe('Rename columns. Variadic positional (old new) pairs.')
  .register(pipelineVerbs, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform ds (rename (price total) (qty count)))' },
    apply: (data, dataset: MaterializedDataset): MaterializedDataset => {
      const map = Object.fromEntries(data.params.map((pair) => [pair.name, pair.params[0].$sym]));
      return withTable(dataset, (t) => t.rename(map));
    },
    // Output schema rewrites each input key under its rename target (if
    // listed) or leaves the key in place; types are preserved verbatim.
    propagateSchema: (data, ctx: PropagateSchemaCtx): Schema => {
      const renames = new Map((data.params as readonly RenamePair[]).map((pair) => [pair.name, pair.params[0].$sym]));
      return schemaFromEntries(
        Array.from(ctx.inputSchema.entries()).map(([name, type]) => [renames.get(name) ?? name, type]),
      );
    },
    // Each pair's `pair.name` is a USAGE of the OLD column. The new
    // name (`pair.params[0].$sym`) is the rename TARGET — a definition,
    // not a usage — so it stays out of the ref set. The pair-name path
    // is `<verbPath>/params/<i>/name` per the spans convention; this is
    // what `normalize.ts` writes for the head of an op-call-shaped pair.
    referencedColumns: (data, basePath): readonly ColumnRefInArg[] =>
      (data.params as readonly RenamePair[]).map((pair, i) => ({
        name: pair.name,
        path: nameKeyPath(paramPath(basePath, i)),
      })),
  });

/**
 * All builtin pipeline-verb schemas. Compose with extension verbs via
 * `createVerbTable`.
 */
export const builtinVerbs = [filter, derive, select, limit, distinct, rename] as const;
/**
 * Pipeline-verb scope — generalises the established two-registry split
 * (`opMeta` + `pipelineVerbs`) into a named, documented scope. The
 * `transform` op below declares this as its `childScopes` member; the
 * auto-generated doc walker (`ExprInstructions`) emits one section for
 * pipeline verbs by virtue of that declaration, no hand-written `<h2>`
 * carve-out required. Future verbs (`aggregate`, `sort`, …) append to
 * `builtinVerbs` and the scope picks them up automatically.
 */
export const pipelineVerbScope: Scope<unknown> = defineScope({
  name: 'Pipeline verbs',
  description: 'Reachable only inside `(transform ds …)`. Each verb consumes the threaded Dataset.',
  registry: pipelineVerbs,
  schemas: builtinVerbs,
});

const transform = z
  .object({
    name: z.literal('transform'),
    params: z.array(z.unknown()).min(1),
  })
  .describe(
    `Apply transform pipeline verbs to a Dataset, threading the running dataset through each verb in order. First positional arg evaluates to the input Dataset; each subsequent positional arg is a verb call (${builtinVerbs.map(opNameOf).join(', ')}).`,
  )
  .register(opMeta, {
    evaluation: 'special',
    effect: 'pure',
    example: { expr: '(transform wells (filter (> production 100)) (limit 5))' },
    // Declares the scope from which `transform`'s positional verb-call
    // args dispatch. The doc walker follows this transitively; runtime
    // dispatch still goes through `pipelineVerbs` via `evalVerbCall`'s
    // `resolveOpCall` invocation (no perf change).
    childScopes: [pipelineVerbScope],
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const [datasetArg, ...verbArgs] = data.params;
      const dsPath = paramPath(ctx.path, 0);
      const { value } = await ctx.evalNode(datasetArg as AstInput, ctx.env, dsPath);
      const initial = expectDataset(value, '(transform <Dataset> <verb> ...)', nodePos(ctx.spans, dsPath));
      // When the source is a bare binding symbol, carry its NAME into
      // verb dispatch so `filter`'s cross-dataset set-ref seam can
      // distinguish a self-reference (`(col <thisName> …)` → per-row
      // accessor) from a foreign binding ref. Non-symbol sources
      // (inline `(col …)` / expressions) have no self-name to clash.
      const srcShape = astShape(datasetArg as AstInput);
      const sourceBindingName = srcShape.kind === 'symbolRef' ? srcShape.sym : undefined;
      const ds = await verbArgs.reduce<Promise<Dataset>>(async (acc, verbAst, idx) => {
        const current = await acc;
        const verbPath = paramPath(ctx.path, idx + 1);
        if (!isOpCall(verbAst)) {
          throw new ShapeError('expected a pipeline verb call', nodePos(ctx.spans, verbPath));
        }
        return ctx.evalVerb(verbAst, current, verbPath, sourceBindingName);
      }, Promise.resolve(initial));
      return { value: ds, env: ctx.env };
    },
  });

/**
 * All builtin top-level op schemas. Compose with extension ops via
 * `createOpTable`.
 */
export const builtins = [
  plus,
  minus,
  times,
  divide,
  mod,
  pow,
  abs,
  neg,
  round,
  floor,
  ceil,
  sqrt,
  log,
  exp,
  minOp,
  maxOp,
  lessThan,
  lessEq,
  greater,
  greaterEq,
  equals,
  notEquals,
  inOp,
  prefix,
  and,
  or,
  not,
  isNull,
  isNotNull,
  coalesce,
  ifOp,
  caseOp,
  nullIf,
  dataset,
  materializeOpForm,
  colOpForm,
  transform,
] as const;
/**
 * Top-level op scope. Anchors the doc walker and the engine's default
 * `NameTable` (`defaultOpTable` in `evaluator.ts` builds from
 * `builtins`). Plugin authors wanting a custom scope (e.g. one
 * augmenting `builtins` with their domain ops) call `defineScope`
 * directly against `opMeta` and pass the result to `ExprInstructions`.
 */
export const topLevelScope: Scope<unknown> = defineScope({
  name: 'Top-level ops',
  description: 'Callable at script top level. Composes via `createOpTable([...builtins, ...])`.',
  registry: opMeta,
  schemas: builtins,
});
